import React, { useState, useEffect } from 'react';
import api, { formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { Plus, Search, Wallet, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDateDisplay } from '../lib/dateUtils';

export default function AdvanceList() {
  const navigate = useNavigate();
  const [advances, setAdvances] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (typeFilter) params.type = typeFilter;
      if (search) params.party_name = search;
      const res = await api.get('/advances', { params });
      setAdvances(res.data.advances || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load advances'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [typeFilter]);

  return (
    <div data-testid="advance-list-page" className="space-y-5 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Advances</h1>
          <p className="text-sm text-slate-500 font-body">Track advance receipts and payments</p></div>
        <button onClick={() => navigate('/advances/new')} className="btn-accent flex items-center gap-2" data-testid="new-advance-btn"><Plus className="w-4 h-4" /> New Advance</button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder="Search party..." className="form-input pl-10 w-60" /></div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="form-input w-40" data-testid="type-filter">
          <option value="">All Types</option>
          <option value="received">Received</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="data-table w-full" data-testid="advance-table">
          <thead><tr><th>S.No.</th><th>Voucher #</th><th>Type</th><th>Date</th><th>Party</th><th className="text-right">Amount</th><th className="text-right">Adjusted</th><th className="text-right">Balance</th><th>Status</th><th>Reference</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="10" className="text-center py-8 text-slate-400">Loading...</td></tr> :
             advances.length === 0 ? <tr><td colSpan="10" className="text-center py-8 text-slate-400">No advances found</td></tr> :
             advances.map((adv, idx) => (
              <tr key={adv.advance_id} data-testid={`advance-row-${adv.advance_id}`}>
                <td className="text-slate-400 text-sm">{idx + 1}</td>
                <td className="font-mono text-sm font-medium text-slate-900">{adv.voucher_number}</td>
                <td><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 w-fit ${adv.type === 'received' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                  {adv.type === 'received' ? <ArrowDownLeft className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                  {adv.type === 'received' ? 'Receipt' : 'Payment'}
                </span></td>
                <td className="text-sm text-slate-600">{formatDateDisplay(adv.date)}</td>
                <td className="text-sm font-medium">{adv.party_name}</td>
                <td className="text-right font-mono text-sm">{formatCurrency(adv.amount)}</td>
                <td className="text-right font-mono text-sm text-slate-500">{formatCurrency(adv.adjusted_amount)}</td>
                <td className="text-right font-mono text-sm font-medium">{formatCurrency(adv.balance_amount)}</td>
                <td><span className={`text-[10px] px-2 py-0.5 rounded-full ${adv.status === 'fully_adjusted' ? 'bg-slate-100 text-slate-500' : 'bg-blue-50 text-blue-700'}`}>{adv.status === 'fully_adjusted' ? 'Adjusted' : 'Active'}</span></td>
                <td className="text-sm text-slate-500 truncate max-w-[150px]">{adv.reference}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 text-right">{total} total advances</p>
    </div>
  );
}
